import { useContext } from "react";
import styled, { css } from "styled-components";
import {
  Button as ButtonPrimitive,
  TooltipTriggerStateContext,
} from "react-aria-components";
import { CoreTooltipIconButton } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import {
  InteractiveInfoIcon,
  InteractiveInfoFilledIcon,
} from "@boi/react-icons";

type ButtonProps = {
  $tooltipRole: "tooltip" | "dialog";
  $variant: CoreTooltipIconButton.Variant;
};

export const Button = styled(ButtonPrimitive)<ButtonProps>`
  ${({ $tooltipRole, $variant }) => {
    const { tokens } = useDesignTokens();
    const { iconButton } = tokens;

    return [
      css`
        all: unset;
        display: grid;
        place-items: center;
        padding: ${iconButton.padding[$variant]};
        margin: -${iconButton.padding[$variant]};
      `,
      $tooltipRole === "dialog" &&
        css`
          &:not([data-disabled]) {
            cursor: pointer;
          }
        `,
    ];
  }}
`;

type ContainerProps = {
  $variant: CoreTooltipIconButton.Variant;
};

export const Container = styled.div<ContainerProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { iconButton } = tokens;

    return css`
      display: grid;
      place-items: center;
      padding: ${iconButton.inner.padding[$variant]};
      border-radius: ${iconButton.inner.radius[$variant]};
      background: ${iconButton.inner.backgroundColor[$variant].base};

      ${Button}[data-focus-visible] && {
        outline-width: ${iconButton.inner.focusRing.width};
        outline-style: ${iconButton.inner.focusRing.style};
        outline-color: ${iconButton.inner.focusRing.color};
        outline-offset: ${iconButton.inner.focusRing.offset.default};
      }
    `;
  }}
`;

type IconProps = {
  $variant: CoreTooltipIconButton.Variant;
};

const Icon = styled(InteractiveInfoIcon)<IconProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { iconButton, tooltipIconButton } = tokens;

    return css`
      width: ${iconButton.inner.icon.width[$variant]};
      height: ${iconButton.inner.icon.height[$variant]};

      & [data-primary-color] {
        color: ${tooltipIconButton.icon.primaryColor.base};
      }

      & [data-secondary-color] {
        color: ${tooltipIconButton.icon.secondaryColor.base};
      }
    `;
  }}
`;

export const TooltipIcon = styled(Icon)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tooltipIconButton } = tokens;

    const tooltipTriggerState = useContext(TooltipTriggerStateContext);
    const isOpen = tooltipTriggerState?.isOpen || false;

    return [
      css`
        ${Button}[data-hovered] &&,
        ${Button}[data-focus-visible] && {
          display: none;
        }
      `,
      !isOpen &&
        css`
          ${Button}[data-hovered] && {
            & [data-primary-color] {
              color: ${tooltipIconButton.icon.primaryColor.hover};
            }
          }
        `,
      isOpen &&
        css`
          display: none;
        `,
    ];
  }}
`;

export const DialogIcon = styled(Icon)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { tooltipIconButton } = tokens;

    return css`
      ${Button}[data-hovered] && {
        & [data-primary-color] {
          color: ${tooltipIconButton.icon.primaryColor.hover};
        }

        & [data-secondary-color] {
          color: ${tooltipIconButton.icon.secondaryColor.hover};
        }
      }

      ${Button}[data-pressed] && {
        display: none;
      }
    `;
  }}
`;

type IconFilledProps = {
  $variant: CoreTooltipIconButton.Variant;
};

const IconFilled = styled(InteractiveInfoFilledIcon)<IconFilledProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { iconButton, tooltipIconButton } = tokens;

    return css`
      display: none;
      width: ${iconButton.inner.icon.width[$variant]};
      height: ${iconButton.inner.icon.height[$variant]};

      & [data-primary-color] {
        color: ${tooltipIconButton.iconFilled.primaryColor.base};
      }

      & [data-secondary-color] {
        color: ${tooltipIconButton.iconFilled.secondaryColor.base};
      }
    `;
  }}
`;

export const TooltipIconFilled = styled(IconFilled)`
  ${() => {
    const tooltipTriggerState = useContext(TooltipTriggerStateContext);
    const isOpen = tooltipTriggerState?.isOpen || false;

    return [
      css`
        ${Button}[data-hovered] &&,
        ${Button}[data-focus-visible] && {
          display: block;
        }
      `,
      isOpen &&
        css`
          display: block;
        `,
    ];
  }}
`;

export const DialogIconFilled = styled(IconFilled)`
  ${() => {
    return css`
      ${Button}[data-pressed] && {
        display: block;
      }
    `;
  }}
`;
